import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter } from "lucide-react";

export interface EnrollmentCapacityChartProps {
  dateRange?: { from: Date; to: Date };
}

export function EnrollmentCapacityChart({ dateRange }: EnrollmentCapacityChartProps) {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  const [filterType, setFilterType] = useState<string>("all");

  // Fetch classes for the filter dropdown
  const { data: classesData } = useQuery({
    queryKey: ['classes', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      const { data, error } = await supabase
        .from('classes')
        .select('id, name, is_event, max_students')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name');
      if (error) throw new Error(error.message);
      return (data || []).map(cls => ({
        id: cls.id as string,
        name: cls.name as string,
        is_event: cls.is_event as boolean,
        max_students: cls.max_students || 0
      }));
    },
    enabled: !!organizationId
  });

  // Fetch enrollments for all classes
  const { data: enrollmentsData } = useQuery({
    queryKey: ['enrollments', organizationId, dateRange],
    queryFn: async () => {
      if (!organizationId) return [];
      let query = supabase
        .from('enrollments')
        .select('class_id, status, created_at')
        .eq('organization_id', organizationId)
        .eq('status', 'active');
      if (dateRange) {
        query = query.gte('created_at', dateRange.from.toISOString()).lte('created_at', dateRange.to.toISOString());
      }
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data || [];
    },
    enabled: !!organizationId
  });

  // Calculate capacity stats based on filter
  const { enrolledSpots, totalCapacity, availableSpots } = useMemo(() => {
    if (!classesData || !enrollmentsData) return { enrolledSpots: 0, totalCapacity: 0, availableSpots: 0 };
    let filteredClasses = classesData;
    if (filterType === "events") {
      filteredClasses = classesData.filter(c => c.is_event);
    } else if (filterType === "classes") {
      filteredClasses = classesData.filter(c => !c.is_event);
    } else if (filterType !== "all") {
      filteredClasses = classesData.filter(c => c.id === filterType);
    }
    const classIds = filteredClasses.map(c => c.id);
    const filteredEnrollments = enrollmentsData.filter(e => classIds.includes(e.class_id));
    const enrolledSpots = filteredEnrollments.length;
    const totalCapacity = filteredClasses.reduce((sum, c) => sum + (c.max_students || 0), 0);
    const availableSpots = totalCapacity - enrolledSpots;
    return { enrolledSpots, totalCapacity, availableSpots };
  }, [classesData, enrollmentsData, filterType]);

  const enrollmentPercentage = totalCapacity > 0 ? Math.round((enrolledSpots / totalCapacity) * 100) : 0;

  // Determine progress bar color based on enrollment percentage
  const getProgressColor = () => {
    if (enrollmentPercentage >= 90) return "danger";
    if (enrollmentPercentage >= 75) return "warning";
    return "success";
  };

  return (
    <Card className="bg-gradient-to-br from-white via-green-50 to-green-100/60 shadow border-0 backdrop-blur-md">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">Enrollment Capacity</CardTitle>
          <div className="flex items-center">
            <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px] h-8 text-sm">
                <SelectValue placeholder="Filter by class/event" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes & Events</SelectItem>
                <SelectItem value="classes">Classes Only</SelectItem>
                <SelectItem value="events">Events Only</SelectItem>
                {classesData?.map(cls => (
                  <SelectItem key={cls.id} value={cls.id}>
                    {cls.name} {cls.is_event ? "(Event)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Overall Enrollment</span>
              <span className="font-medium">{enrollmentPercentage}% filled</span>
            </div>
            <Progress 
              value={enrollmentPercentage} 
              className="h-2" 
              indicatorColor={getProgressColor()} 
            />
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="space-y-2">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Enrolled Spots</span>
                  <span className="text-2xl font-bold text-green-900">{enrolledSpots}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Available Spots</span>
                  <span className="text-2xl font-bold text-blue-900">{availableSpots}</span>
                </div>
              </div>
              <div className="space-y-2 col-span-2">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Total Capacity</span>
                  <span className="text-2xl font-bold text-purple-900">{totalCapacity}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
